import { FormattedMessage } from 'react-intl';

// common
import { Box, Typography } from '@mui/material';
import LogoAvatar from 'common/components/LogoAvatar';
import MuiLoading from 'common/components/MuiLoading';
import OperationCodeField from 'common/components/OperationCodeField';
import SchemaForm from 'common/SchemaForm';
import QrDisplayer from 'libraries/qr-code';

// screen
import BackToSuppliersPageButton from 'modules/suppliers/detail/components/BackToSuppliersPageButton';
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import { buildFullAddress } from 'modules/suppliers/helpers/supplierAddress';
import { GeneralDetails } from 'modules/suppliers/services/useSupplierGeneralDetails';

const logoSize = 144;

/**
 * Tab "Thông tin chung" của chi tiết HSNL NCC ở QR NCC TPAT (thiết kế node 7223-26633).
 *
 * Bố cục 2 cột: Tên HSNL NCC, Logo, Người đại diện, SĐT | Mã chuyển đổi số, Mã QR, Email, Địa chỉ.
 * Khác Quick Search đúng một chỗ: có thêm Mã QR.
 *
 * Địa chỉ là MỘT ô đầy đủ (không còn tách Tỉnh/TP, Xã/phường, Quận cũ thành ô riêng) — đúng như
 * cột địa chỉ ngoài danh sách.
 */
const GeneralDetailsPanel = () => {
  const { generalDetails } = useTabsData();

  if (!generalDetails) return <MuiLoading />;

  // SchemaForm đọc theo key của formData nên phải bơm sẵn địa chỉ đã ghép vào một trường riêng.
  const values = {
    ...generalDetails,
    fullAddress: buildFullAddress({
      addressDetail: generalDetails.addressDetail,
      wardName: generalDetails.wardName,
      cityName: generalDetails.cityName,
    }),
  };

  return (
    <>
      <SchemaForm<GeneralDetails & { fullAddress: string }>
        formHookProps={{ values }}
        globalProps={{ readOnly: true }}
        schema={{
          fields: ({
            formProps: {
              intl: { formatMessage },
            },
          }) => {
            return {
              name: {
                mode: 'text-field',
                propsWrapper: { tablet: 6 },
                label: formatMessage({ id: 'supplier.generalDetails.supplierName' }),
                placeholder: formatMessage({ id: 'supplier.generalDetails.supplierName' }),
                rules: { required: true },
              },
              operationCode: {
                mode: 'raw',
                propsWrapper: { tablet: 6 },
                render: (params) => (
                  <OperationCodeField
                    {...params}
                    label={formatMessage({ id: 'supplier.generalDetails.operationCode' })}
                  />
                ),
              },
              logo: {
                mode: 'raw',
                propsWrapper: { tablet: 6 },
                render: () => {
                  return (
                    <>
                      <Typography sx={{ fontSize: 12 }}>
                        <FormattedMessage id="supplier.generalDetails.logo" />:
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <LogoAvatar
                          alt="company logo"
                          src={generalDetails.logo}
                          size={logoSize}
                        />
                      </Box>
                    </>
                  );
                },
                rules: { required: true },
              },
              qrCode: {
                mode: 'raw',
                propsWrapper: { tablet: 6 },
                render: () => {
                  return (
                    <>
                      <Typography sx={{ fontSize: 12 }}>
                        <FormattedMessage id="supplier.generalDetails.qrCode" />:
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <QrDisplayer value={generalDetails.qrCode} size={logoSize} />
                      </Box>
                    </>
                  );
                },
              },
              representative: {
                mode: 'text-field',
                propsWrapper: { tablet: 6 },
                label: formatMessage({ id: 'supplier.generalDetails.representative' }),
                placeholder: formatMessage({ id: 'supplier.generalDetails.representative' }),
              },
              email: {
                mode: 'text-field',
                propsWrapper: { tablet: 6 },
                label: formatMessage({ id: 'supplier.generalDetails.email' }),
                placeholder: formatMessage({ id: 'supplier.generalDetails.email' }),
              },
              phone: {
                mode: 'text-field',
                propsWrapper: { tablet: 6 },
                label: formatMessage({ id: 'supplier.generalDetails.phone' }),
                placeholder: formatMessage({ id: 'supplier.generalDetails.phone' }),
                rules: { required: true },
              },
              fullAddress: {
                mode: 'text-field',
                propsWrapper: { tablet: 6 },
                label: formatMessage({ id: 'supplier.generalDetails.address' }),
                placeholder: formatMessage({ id: 'supplier.generalDetails.address' }),
                rules: { required: true },
              },
            };
          },
        }}
      />
      <BackToSuppliersPageButton />
    </>
  );
};

export default GeneralDetailsPanel;
